import { createWeakMapSelector } from 'app-components/utils/core/reselect';
import { Map as ImmutableMap, List as ListImmutable } from 'immutable';
import { createSelector } from 'reselect';

import { DateTime } from 'luxon';
import { accountNodeUtils } from '../account-nodes';
import { accountsSelectors } from '../accounts';
import { categoriesSelectors } from '../categories';
import { chartOfAccountsTypes } from '../chart-of-accounts';
import { selectorsHelpers } from '../core';
import { dateSelectors } from '../date';
import { transactionsSelectors, transactionsTypes, transactionsUtils } from '../transactions';

const txnIsPostedAndNotPending = (txn) => txn.source !== 'SCHEDULED_TRANSACTION_PENDING' && txn.state !== 'PENDING';

const epoch = '1970-01-01';
const sixMonthFallback = DateTime.now().plus({ months: 6 }).toISODate();

export const getScheduledInstances = createWeakMapSelector(
  [
    transactionsSelectors.getResourcesById,
    (_state, start) => (typeof start === 'object' && start.toISODate?.()) || start || epoch,
    (_state, _start, end) => (typeof end === 'object' && end.toISODate?.()) || end || sixMonthFallback,
    (_state, _start, _end, accountIds) => accountIds,
    (_state, _start, _end, _accountIds, txnTypes = transactionsSelectors.txnTypesCashAndPlanned) => txnTypes,
    (_state, _start, _end, _accountIds, _txnTypes, excludePosted = false) => excludePosted,
    dateSelectors.getCurrentDateString,
    selectorsHelpers.createTrimSelector(accountsSelectors.getAccountsById, (account) => ({
      isClosed: account.isClosed,
    })),
    accountsSelectors.getGoalAccountIds,
  ],
  (transactions, start, end, accountIds, txnTypes, excludePosted, today, accountsById, goalAccountIds) =>
    transactions
      .filter((txn) => {
        const refundState = transactionsUtils.getRefundState(txn, today);
        if (
          refundState === transactionsUtils.RefundState.EXPECTED ||
          refundState === transactionsUtils.RefundState.OVERDUE
        ) {
          return true; // keep expected and overdue refunds regardless of date
        }

        if (!transactionsSelectors.transactionOfTypes(txn, goalAccountIds, txnTypes)) {
          return false;
        }

        const txnDate = txn.postedOn || txn.stDueOn;

        const isFutureManual =
          ((txn.source === 'USER_ENTERED' || txn.source === 'QCS_IMPORT') && txnDate > today) ||
          transactionsUtils.isPlannedA2ATransfer(txn); // treat planned A2A transfers as future manual txns

        if (excludePosted && !isFutureManual && txn.source !== 'SCHEDULED_TRANSACTION_PENDING') {
          return false;
        }

        if (
          (accountIds === undefined || accountIds.includes(txn.accountId)) &&
          accountsById.get(txn.accountId) &&
          !accountsById.get(txn.accountId).isClosed
        ) {
          if (!txnDate || txnDate < start || txnDate > end) {
            return false;
          }
          if (isFutureManual) {
            return true;
          }
          if ((txn.isBill || txn.isSubscription || txn.stModelId) && txn.subtype !== 'A2A_TRANSFER') {
            // do we still need the A2A transfer exclusion here?
            return true;
          }
        }
        return false;
      })
      .sort((a, b) => {
        const aVal = a.postedOn || a.stDueOn;
        const bVal = b.postedOn || b.stDueOn;
        if (aVal > bVal) return 1;
        if (bVal > aVal) return -1;
        return 0;
      }),
);

export const bucketizeTransactionsByDate = createSelector(
  (_state, upcomingTxns) => upcomingTxns,
  dateSelectors.getCurrentDateString,
  (transactions, today) => {
    const past = [];
    const next = [];
    transactions.forEach((txn) => {
      const txnDate = txn.postedOn || txn.stDueOn;
      if (txnDate >= today) {
        next.push(txn);
      } else {
        past.push(txn);
      }
    });

    return ImmutableMap([
      ['PAST', ListImmutable(past)],
      ['NEXT', ListImmutable(next)],
    ]);
  },
);
export const bucketizeTransactionsByType = createSelector(
  (_state, upcomingTxns) => upcomingTxns,
  categoriesSelectors.getIncomeCOAIds,
  (transactions, incomeCoas) => {
    const bills = [];
    const income = [];
    const refunds = [];
    const subscriptions = [];
    const transfers = [];
    let incomeAmount = 0;
    let expensesAmount = 0;
    let numOfExcludedTxns = 0;

    transactions.forEach((txn) => {
      txn.isExcludedFromReports && (numOfExcludedTxns += 1);
      if (transactionsUtils.isIncomeFromTxn(txn, incomeCoas)) {
        !txn.isExcludedFromReports && (incomeAmount += txn.amount);
        income.push(txn);
      } else if (txn.subtype === 'REFUND') {
        !txn.isExcludedFromReports && (expensesAmount += txn.amount);
        refunds.push(txn);
      } else if (txn.isSubscription) {
        !txn.isExcludedFromReports && (expensesAmount += txn.amount);
        subscriptions.push(txn);
      } else if (transactionsUtils.isUITransferTxn(txn)) {
        !txn.isExcludedFromReports && (txn.amount > 0 ? (incomeAmount += txn.amount) : (expensesAmount += txn.amount));
        transfers.push(txn);
      } else {
        !txn.isExcludedFromReports && (expensesAmount += txn.amount);
        bills.push(txn);
      }
    });
    const typeAccounts = ImmutableMap([
      ['INCOME', ListImmutable(income)],
      ['BILLS', ListImmutable(bills)],
      ['SUBSCRIPTIONS', ListImmutable(subscriptions)],
      ['REFUNDS', ListImmutable(refunds)],
      ['TRANSFERS', ListImmutable(transfers)],
    ]);
    return { typeAccounts, incomeAmount, expensesAmount, numOfExcludedTxns };
  },
);
export const bucketizeTransactionsByAccountName = createSelector(
  (_state, upcomingTxns) => upcomingTxns,
  accountsSelectors.getAccountsById,
  (upcomingTxns, getAccountsById) =>
    ImmutableMap().withMutations((transactionsByAccountIdMutable) =>
      upcomingTxns?.forEach((txn) => {
        const accountName = getAccountsById.get(txn.accountId)?.name || 'Unknown';
        return transactionsByAccountIdMutable.set(
          accountName,
          (transactionsByAccountIdMutable.get(accountName) || ListImmutable()).push(txn),
        );
      }),
    ),
);
export const sortUpcomingTxns = (state, allUpcomingTransactions, sortType) => {
  switch (sortType) {
    case 'due-date':
      return bucketizeTransactionsByDate(state, allUpcomingTransactions);
    case 'account':
      return bucketizeTransactionsByAccountName(state, allUpcomingTransactions); // by account name is better
    case 'type':
      return bucketizeTransactionsByType(state, allUpcomingTransactions).typeAccounts;
    default:
      return {};
  }
};

export const getPastDueTxns = createSelector(
  (state) => transactionsSelectors.getTransactionsOfTypes(state, transactionsSelectors.txnTypesCashAndPlanned),
  (_state, start) => start.toISODate(),
  dateSelectors.getCurrentDateString,
  accountsSelectors.getAccountsById,
  (transactions, startDate, today, accountsById) => {
    const past = [];
    transactions.forEach((txn) => {
      const txnDate = txn.postedOn || txn.stDueOn;
      if (
        !txnIsPostedAndNotPending(txn) &&
        txnDate >= startDate &&
        txnDate < today &&
        (txn.source === 'SCHEDULED_TRANSACTION_PENDING' || txn.source === 'USER_ENTERED') &&
        (txn.isBill ||
          txn.isSubscription ||
          txn.stModelId ||
          txn.subtype === transactionsTypes.TransactionSubType.A2aTransfer) &&
        accountsById.get(txn.accountId) &&
        !accountsById.get(txn.accountId).isClosed
      ) {
        past.push(txn);
      } else {
        const refundState = transactionsUtils.getRefundState(txn);
        switch (refundState) {
          case transactionsUtils.RefundState.OVERDUE:
            past.push(txn);
            break;
          case transactionsUtils.RefundState.EXPECTED:
          case transactionsUtils.RefundState.COMPLETE:
          default:
            break;
        }
      }
    });
    return past.sort((a, b) => new Date(a.postedOn || a.stDueOn) - new Date(b.postedOn || b.stDueOn));
  },
);

export const untilNextPaycheck = createSelector(
  (state) => transactionsSelectors.getTransactionsOfTypes(state, transactionsSelectors.txnTypesCashAndRefund),
  dateSelectors.getCurrentDateString,
  categoriesSelectors.getIncomeCOAIds,
  (transactions, today, incomeCoas) => {
    const futureIncome = transactions
      .filter((txn) => {
        const txnDate = txn.postedOn || txn.stDueOn;
        if (transactionsUtils.isIncomeFromTxn(txn, incomeCoas) && txnDate > today) {
          return true;
        }
        return false;
      })
      .sort((a, b) => new Date(a.postedOn || a.stDueOn) - new Date(b.postedOn || b.stDueOn));
    if (futureIncome.size > 0) {
      const isoDateString = futureIncome.first().postedOn || futureIncome.first().stDueOn;
      return DateTime.fromISO(isoDateString);
    }
    return null;
  },
);

const sortByName = (a, b) => {
  const accA = a?.name?.toLowerCase();
  const accB = b?.name?.toLowerCase();
  if (accA > accB) {
    return 1;
  }
  if (accA < accB) return -1;
  return 0;
};

const sortByOrder = (a, b) => {
  const accA = accountNodeUtils.getL2GroupingData(null, a.first())?.displayOrder;
  const accB = accountNodeUtils.getL2GroupingData(null, b.first())?.displayOrder;
  if (accA > accB) {
    return 1;
  }
  if (accA < accB) return -1;
  return 0;
};

export const getAccountsForProjectedCashFlow = createSelector(accountsSelectors.getAccountsById, (accountsById) =>
  accountsById.filter((acct) => acct.type !== 'INVESTMENT' && !acct.isClosed).sort(sortByName),
);

// this selector returns the accounts used in the upcoming cash flow graph legend
export const getAccountIdsForProjectedCashFlow = createSelector(getAccountsForProjectedCashFlow, (accounts) => ({
  groupedAccounts: accounts
    .toList()
    .groupBy((acc) => accountNodeUtils.getL2GroupingData(null, acc)?.displayLabel)
    .sort(sortByOrder),
  accountIds: ListImmutable([...accounts.keys()]),
}));

const transferCheck = (txn, model) => {
  if (txn.coa) {
    if (txn.coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) {
      // make sure only one side of the transfer displays based on matching the model
      return model && Math.sign(model.transaction?.amount) === Math.sign(txn.amount);
    }
    return true;
  }
  if (txn.split) {
    return true;
  }
  return false;
};

export const getModelHistory = createSelector(
  transactionsSelectors.getTransactionsById,
  (_state, model /* ScheduledTransaction */) => model,
  (transactions, model) => {
    const monthCutoff = DateTime.local().endOf('month');

    let oldestPending;
    let pastModelTxns = ImmutableMap();
    if (model) {
      pastModelTxns =
        transactions?.filter((txn /* Transaction */) => {
          if (txn.stModelId === model.id && transferCheck(txn, model)) {
            const txnDate = transactionsUtils.dateTimeFromTxn(txn);
            if (
              txn.source === 'SCHEDULED_TRANSACTION_PENDING' &&
              (!oldestPending || txnDate < transactionsUtils.dateTimeFromTxn(oldestPending))
            ) {
              oldestPending = txn;
            }
            return txnDate < monthCutoff;
          }
          return false;
        }) || ImmutableMap();
    }
    return {
      history: pastModelTxns.sort((a, b) => new Date(b.postedOn) - new Date(a.postedOn)),
      oldestPending,
    };
  },
);

export const generateChartData = createSelector(
  (history) => history,
  (_history, negator) => negator,
  (history, negator) => {
    if (!history) return { chartData: [], average: 0 };

    const pastCutoff = DateTime.local().startOf('month').minus({ months: 5 });
    const periods = [];
    for (let i = 0; i < 6; i++) {
      const currentMonth = pastCutoff.plus({ months: i });
      periods.push({
        month: currentMonth.toFormat('MMM').toUpperCase(),
        totalAmount: 0,
        currentAmount: 0,
      });
    }

    history.forEach((txn) => {
      const txnDate = transactionsUtils.dateTimeFromTxn(txn);
      if (txnDate && txnDate >= pastCutoff) {
        const periodIndex = periods.findIndex((period) => period.month === txnDate.toFormat('MMM').toUpperCase());
        const amt = Number(txn.amount);
        if (periodIndex > -1) {
          periods[periodIndex].totalAmount += amt;
          if (txnIsPostedAndNotPending(txn)) {
            // posted
            periods[periodIndex].currentAmount += amt;
          }
        }
      }
    });

    let sum = 0;
    let activeMonths = 0;
    let foundActiveMonth = false;
    periods.forEach((period, i) => {
      if (period.totalAmount || foundActiveMonth) {
        activeMonths += 1;
        foundActiveMonth = true;
      }
      const newAmount = period.totalAmount * negator;
      sum += newAmount * negator; // Double negate to cancel it out
      periods[i].totalAmount = newAmount;
      periods[i].currentAmount *= negator;
    });
    if (activeMonths < 1) activeMonths = 1;
    const average = Math.round(sum / activeMonths);

    return { chartData: periods, average, sum, activeMonths };
  },
);
